import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import { classNames } from "primereact/utils";
import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import PrimeConfirmDialog from "../../components/dialog/PrimeConfirmDialog";
import NoInfo from "../../components/NoInfo";
import PrimeTableFooter from "../../components/PrimeTableFooter";
import { useEditableFlag } from "../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../store/loadingStore";
import NumberUtil from "../../utility/NumberUtil";
import {
  deleteEquipmentProcess,
  getCompanyPlaceList,
  queryEquipmentProcessList,
} from "../../utility/service/cbam-service";
import { checkDpSuccess, getCompanyType, getFormErrorMessage } from "../../utility/service/tool-service";

export default function EquipmentProcessSettingsIndex() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    action,
    setAction,
    itemObj,
    setItemObj,
    currentCompanySeq,
    setCurrentCompanySeq,
    selectPlaceList,
    setSelectPlaceList,
    currentCompanyName,
    setCurrentCompanyName,
  } = useOutletContext();
  const [, loading] = useContext(SystemLoading);
  const [companyPlaceList, setCompanyPlaceList] = useState([]);
  const [rows, setRows] = useState([]);
  const [lastRowsSeq, setLastRowsSeq] = useState("");
  const [existPlaceList, setExistPlaceList] = useState([]);
  const [delDialog, setDelDialog] = useState(false);
  const [delObj, setDelObj] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [tooltipTarget, setTooltipTarget] = useState(null);
  const isEditable = useEditableFlag("F10601");

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    trigger,
    handleSubmit,
    clearErrors,
    setError,
    resetField,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  useEffect(() => {
    axios_getCompanyPlaceList();
  }, []);

  useEffect(() => {
    const isFromChildPage = sessionStorage.getItem("fromEquipmentProcessSettingsChildPage");
    if (currentCompanySeq !== "" && isFromChildPage) {
      axios_queryEquipmentProcessList({ companySeq: currentCompanySeq });
      setValue("companySeq", currentCompanySeq);
      sessionStorage.removeItem("fromEquipmentProcessSettingsChildPage");
    }
  }, [currentCompanySeq]);

  //組織邊界廠商清單
  const axios_getCompanyPlaceList = () => {
    checkLoading(loading);
    getCompanyPlaceList()
      .then((res) => {
        if (checkDpSuccess(res)) {
          const data = res.data.RespBody.companyPlaceList || [];
          setCompanyPlaceList(data);
          if (getCompanyType() === "A" && data.length > 0) {
            const firstCompany = data[0];
            setValue("companySeq", firstCompany.companySeq);
            setCurrentCompanyName(firstCompany.companyName);
            const match = data.find((item) => item.companySeq === firstCompany.companySeq);
            setExistPlaceList(match?.placeList);
            setSelectPlaceList(match);
            setCurrentCompanySeq(firstCompany.companySeq);
            axios_queryEquipmentProcessList({ companySeq: firstCompany.companySeq });
          }
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  // 設備製程列表查詢
  const axios_queryEquipmentProcessList = (reqBody, readMore = false) => {
    checkLoading(loading);
    queryEquipmentProcessList(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          const data = res.data.RespBody.equipmentList || [];
          if (data.length > 0) {
            setLastRowsSeq(data[data.length - 1].equipmentId);
            setRows((prev) => (readMore ? [...prev, ...data] : [...data]));
          } else if (!readMore) {
            setRows([]);
          }
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  // 查看更多
  const btnReadMore = () => {
    let reqBody = {
      companySeq: getValues("companySeq"),
      equipmentId: lastRowsSeq,
    };
    axios_queryEquipmentProcessList(reqBody, true);
  };

  const onSubmit = (data) => {
    axios_queryEquipmentProcessList({ companySeq: data.companySeq });
  };

  const actionBodyTemplate = (rowData, props) => {
    if (rowData) {
      return (
        <>
          <Button
            tooltip={t(`button.detail`)}
            tooltipOptions={{ position: "top" }}
            rounded
            text
            severity="secondary"
            className="p-button-icon-only"
            onClick={() => {
              actionItem(rowData, "detail");
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-eye" />
          </Button>

          <Button
            disabled={!isEditable}
            tooltip={t("button.delete")}
            tooltipOptions={{ position: "top" }}
            rounded
            text
            severity="secondary"
            className="p-button-icon-only"
            onClick={() => {
              // deleteItem(rowData, "E");
              setDelDialog(true);
              setDelObj(rowData);
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-trash" />
          </Button>
        </>
      );
    }
  };

  // 設備製程詳情新增/編輯
  const actionItem = (data = {}, action = "create") => {
    setItemObj(data);
    setAction(action);
    navigate("detail");
  };

  // 設備製程詳情刪除
  const axios_deleteEquipmentProcess = () => {
    checkLoading(loading);
    deleteEquipmentProcess({ equipmentId: delObj?.equipmentId })
      .then((res) => {
        if (checkDpSuccess(res)) {
          axios_queryEquipmentProcessList({ companySeq: selectPlaceList?.companySeq });
          setDelDialog(false);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  // 切換某列展開
  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  // 新增按鈕 Column body
  const collapseButtonBody = (rowData) => {
    const rowKey = rowData.equipmentId;
    const isExpanded = expandedRows[rowKey];
    const list = rowData.equipmentDetailList || [];

    if (list.length <= 3) return null; // 少於兩個不用按鈕

    return (
      <Button
        icon={isExpanded ? "pi pi-chevron-down" : "pi pi-chevron-right"}
        className="p-button-link p-0"
        style={{
          fontSize: "0.7rem",
          padding: "0.2rem",
          width: "1.2rem",
          height: "1.2rem",
        }}
        onClick={() => toggleRow(rowKey)}
      />
    );
  };

  // 調整 renderWithCollapse 右側文字不再包含按鈕
  const renderWithCollapse = (rowData, renderItem, textAlign = "left") => {
    const rowKey = rowData.equipmentId;
    const isExpanded = expandedRows[rowKey];
    const list = rowData.equipmentDetailList || [];
    const showList = isExpanded ? list : list.slice(0, 3);

    return (
      <div>
        {showList.map((item, idx) => (
          <div
            key={idx}
            className="text-ellipsis"
            style={{
              textAlign: textAlign,
              lineHeight: "1.5rem",
              minHeight: "1.5rem",
            }}
          >
            {renderItem(item)}
          </div>
        ))}
      </div>
    );
  };

  const rawMaterialBody = (rowData) => renderWithCollapse(rowData, (item) => item.sourceName);

  const energyPerTonProductBody = (rowData) =>
    renderWithCollapse(rowData, (item) => <div>{NumberUtil.formatWithCommas(item.consumptionPerTon)}</div>, "right");

  const consumptionUnitBody = (rowData) => renderWithCollapse(rowData, (item) => item.consumptionUnit);

  const tooltipBodyTemplate = (rowData, props) => {
    const tooltipContent = rowData?.[props?.field] || "";
    if (!tooltipContent) return null;

    // 截斷顯示的文字
    const displayText = tooltipContent.length > 15 ? tooltipContent.substring(0, 15) + "..." : tooltipContent;

    return (
      <span
        ref={(el) => setTooltipTarget(el)}
        data-pr-tooltip={tooltipContent} // Tooltip 顯示完整文字
        data-pr-position="bottom"
        className="nameBody text-ellipsis"
      >
        {displayText}
      </span>
    );
  };

  return (
    <>
      {/*上半區*/}
      <div className="p-card bg-gray">
        <div className="p-card-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid">
              <div className="col-12 lg:col-12">
                <label htmlFor="placeName" className="form-label required">
                  {t("organName")}
                </label>
                <Controller
                  name="companySeq"
                  control={control}
                  rules={{ required: { value: true, message: t("validation.required") } }}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      loading={loading.toString() === "true" ? 1 : 0}
                      filter
                      disabled={getCompanyType() === "A"}
                      appendTo={"self"}
                      optionLabel="companyName"
                      optionValue="companySeq"
                      value={field.value}
                      virtualScrollerOptions={{ itemSize: 40 }}
                      defaultValue={""}
                      placeholder={t("plz_chs")}
                      name="companySeq"
                      control={control}
                      options={companyPlaceList}
                      className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                      onChange={(event) => {
                        field.onChange(event.value);
                        setCurrentCompanySeq(event.value);
                        const filterData = companyPlaceList?.filter((item) => item.companySeq === event.value)[0];
                        setSelectPlaceList(filterData);
                        setCurrentCompanyName(filterData.companyName);
                        axios_queryEquipmentProcessList({ companySeq: event.value });
                      }}
                    />
                  )}
                />
                {getFormErrorMessage(errors, "companySeq")}
              </div>
            </div>

            <div className="col-12 gap-2 flex-all-center">
              <Button type="submit" label={t("button.query")} />
              <Button
                outlined
                type="button"
                disabled={currentCompanySeq === "" || !isEditable}
                onClick={() => {
                  actionItem();
                }}
                label={t("button.create")}
              />
            </div>
          </form>
        </div>
      </div>
      {/*下半區*/}
      <div className="p-card">
        <div className="p-card-body">
          <DataTable
            stripedRows
            resizableColumns
            className="white-data-table hover-highlight no-shadow"
            footer={
              rows?.length > 0 && <PrimeTableFooter rowDataList={rows} isShowMore={true} btnReadMore={btnReadMore} />
            }
            emptyMessage={<NoInfo panel="table" />}
            value={rows}
          >
            <Column header={t("processName")} field="processName" body={tooltipBodyTemplate}></Column>
            <Column header={t("equipmentName")} field="equipmentName" body={tooltipBodyTemplate}></Column>
            <Column header={t("place_name")} field="placeName" body={tooltipBodyTemplate}></Column>
            <Column header=" " body={collapseButtonBody} />
            <Column header={t("rawMaterial")} body={rawMaterialBody} bodyStyle={{ maxWidth: "200px" }} />
            <Column header={t("energyPerTonProduct")} body={energyPerTonProductBody} />
            <Column header={t("unit")} body={consumptionUnitBody} />
            <Column
              header={`${t("directEmissions")}(tCO₂e/t)`}
              field="directEmissions"
              body={(rowData) => NumberUtil.formatWithCommas(rowData.directEmissions)}
              style={{ textAlign: "right" }}
              headerStyle={{ textAlign: "right" }}
            ></Column>
            <Column
              header={`${t("indirectEmissions")}(tCO₂e/t)`}
              field="indirectEmissions"
              body={(rowData) => NumberUtil.formatWithCommas(rowData.indirectEmissions)}
              style={{ textAlign: "right" }}
              headerStyle={{ textAlign: "right" }}
            ></Column>
            <Column header={t("action_type")} body={actionBodyTemplate}></Column>
          </DataTable>
        </div>
      </div>

      {tooltipTarget && <Tooltip mouseTrack target=".nameBody" position="bottom" event="hover" />}

      {/*刪除*/}
      <PrimeConfirmDialog
        visible={delDialog}
        onClose={() => {
          setDelDialog(false);
          setDelObj({});
        }}
        onConfirm={() => {
          axios_deleteEquipmentProcess();
        }}
        title={t("button.delete")}
        bodyTemplate={
          <>
            <div className="form-label">
              {t("processName")}: {delObj?.processName}
            </div>
            <div className="form-label">
              {t("equipmentName")}: {delObj?.equipmentName}
            </div>
            <div className="form-label">
              {t("place_name")}: {delObj?.placeName}
            </div>
          </>
        }
      />
    </>
  );
}
